import { createContext, useContext, useMemo, useState } from "react";

import Service from "../api/service";

const StaticContext = createContext(null);

export const useStaticContext = () => {
  const v = useContext(StaticContext);
  if (!v) {
    throw new Error("useStaticContext must be used inside StaticProvider");
  }
  return v;
};

// Tiny helper: GET `url` and pass `response.data` into `setter`. Used by the
// many fetchFooter/fetchTerms/… methods that all follow the same shape.
const fetchInto = async (url, setter) => {
  const response = await Service.get(url);
  if (response?.data !== undefined) setter(response.data);
  return response;
};

export const StaticProvider = ({ children }) => {
  const [footer, setFooter] = useState(null);
  const [aiChatInfo, setAiChatInfo] = useState(null);
  const [terms, setTerms] = useState(null);
  const [privacy, setPrivacy] = useState(null);
  const [service, setService] = useState(null);
  const [stats, setStats] = useState(null);
  const [report, setReport] = useState(null);
  const [emailTemplates, setEmailTemplates] = useState(null);

  const fetchFooter = () => fetchInto("/api/v1/footer", setFooter);
  const fetchAiChatInfo = () => fetchInto("/api/v1/ai_chat_info", setAiChatInfo);
  const fetchTerms = () => fetchInto("/api/v1/terms", setTerms);
  const fetchPrivacy = () => fetchInto("/api/v1/privacy", setPrivacy);
  const fetchService = () => fetchInto("/api/v1/service", setService);

  const updateStaticContent = (type, id, data) =>
    Service.put(`/api/v1/${type}/${id}`, data);

  const getStats = async ({ year, month, data }) => {
    const response = await Service.post(
      `/api/v1/stats/content/admin/?year=${year}&month=${month}`,
      data,
    );
    if (response?.data !== undefined) setStats(response.data);
    return response;
  };

  const getCompanyStats = async ({ data }) => {
    const response = await Service.post("/api/v1/stats/content/company", data);
    if (response?.data !== undefined) setStats(response.data);
    return response;
  };

  const getCustomSearch = async ({ search_key, type }) => {
    const { data } = await Service.get(
      `/api/v1/search/custom/admin/?search_key=${search_key}&page=1&pageSize=100&type=${type}`,
    );
    return { data };
  };

  const getFullReport = ({ year, month }) =>
    fetchInto(`/api/v1/stats/report/${year}/${month}`, setReport);

  const getEmailTemplates = async () => {
    const response = await Service.get("/api/v1/templates");
    if (response?.data?.data !== undefined) setEmailTemplates(response.data.data);
    return response;
  };

  const updateOneEmailTemplate = ({ id, data }) =>
    Service.put(`/api/v1/admin/templates/update/${id}`, data);

  const updateTerms = ({ id, data }) =>
    Service.put(`/api/v1/terms/${id}`, data);

  const updatePrivacy = ({ id, data }) =>
    Service.put(`/api/v1/privacy/${id}`, data);

  const value = useMemo(
    () => ({
      stats,
      setStats,
      footer,
      aiChatInfo,
      terms,
      privacy,
      service,
      report,
      emailTemplates,
      fetchFooter,
      fetchAiChatInfo,
      fetchTerms,
      fetchPrivacy,
      fetchService,
      updateStaticContent,
      getStats,
      getCompanyStats,
      getCustomSearch,
      getFullReport,
      getEmailTemplates,
      updateOneEmailTemplate,
      updateTerms,
      updatePrivacy,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [stats, footer, aiChatInfo, terms, privacy, service, report, emailTemplates],
  );

  return (
    <StaticContext.Provider value={value}>{children}</StaticContext.Provider>
  );
};
